import React from 'react';
import { CheckIcon, XIcon, ArrowRightIcon, ZapIcon, DollarSignIcon, ClockIcon, TrendingUpIcon, UsersIcon, SettingsIcon } from 'lucide-react';
const IntercomComparisonPage = () => {
  return <div className="bg-gray-50" data-id="element-3604">
      {/* Hero Section */}
      <section className="relative py-20 overflow-hidden bg-white" data-id="element-3605">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50 opacity-50" data-id="element-3606"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-3607">
          <div className="text-center max-w-4xl mx-auto" data-id="element-3608">
            <div className="inline-flex items-center gap-2 bg-green-100 text-green-800 px-4 py-2 rounded-full text-sm font-semibold mb-6" data-id="element-3609">
              <ZapIcon className="h-4 w-4" data-id="element-3610" />
              Fast-Growing • Trusted by 1,500+ Businesses
            </div>
            <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6" data-id="element-3611">
              Why E-Commerce Leaders Are{' '}
              <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent" data-id="element-3612">
                Switching to ShoPilots
              </span>
            </h1>
            <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto" data-id="element-3613">
              See how ShoPilots delivers better conversions, simpler pricing,
              and an easier experience compared to Intercom.
            </p>
            <div className="flex gap-4 justify-center flex-wrap" data-id="element-3614">
              <a href="https://chat.shopilots.com/signup?product=shopilots" target="_blank" rel="noopener noreferrer" className="inline-flex items-center px-8 py-4 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg font-semibold hover:from-blue-700 hover:to-purple-700 shadow-lg hover:shadow-xl transition-all duration-300" data-id="element-3615">
                Start Free Trial
                <ArrowRightIcon className="ml-2 h-5 w-5" data-id="element-3616" />
              </a>
              <a href="#comparison" className="inline-flex items-center px-8 py-4 bg-white text-gray-700 rounded-lg font-semibold border-2 border-gray-300 hover:border-blue-600 hover:text-blue-600 transition-all duration-300" data-id="element-3617">
                See Full Comparison
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Transfer Banner */}
      <div className="bg-gradient-to-r from-green-500 to-emerald-600 py-5" data-id="element-3618">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-3619">
          <p className="text-white text-center font-medium" data-id="element-3620">
            <strong data-id="element-3621">🎁 Easy Migration:</strong> We offer free account transfer
            to help you try ShoPilots risk-free. Keep your data, conversations,
            and integrations intact.
          </p>
        </div>
      </div>

      {/* Stats Section */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 -mt-12 relative z-20" data-id="element-3622">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6" data-id="element-3623">
          {[{
          number: '+25%',
          label: 'Conversion Rate Lift'
        }, {
          number: '90%',
          label: 'Support Cost Reduction'
        }, {
          number: '10M+',
          label: 'Monthly Conversations'
        }, {
          number: '1,500+',
          label: 'Growing Businesses'
        }].map((stat, index) => <div key={index} className="bg-white rounded-2xl p-6 text-center shadow-lg hover:shadow-xl transition-shadow" data-id="element-3624">
              <div className="text-4xl font-bold text-blue-600 mb-2" data-id="element-3625">
                {stat.number}
              </div>
              <div className="text-sm text-gray-600 font-medium" data-id="element-3626">
                {stat.label}
              </div>
            </div>)}
        </div>
      </div>

      {/* Main Comparison Section */}
      <section id="comparison" className="py-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-3627">
        <div className="text-center mb-12" data-id="element-3628">
          <h2 className="text-4xl font-bold text-gray-900 mb-4" data-id="element-3629">
            Side-by-Side Comparison
          </h2>
          <p className="text-xl text-gray-600" data-id="element-3630">
            See exactly where ShoPilots outperforms Intercom
          </p>
        </div>

        <div className="bg-white rounded-3xl shadow-2xl overflow-hidden" data-id="element-3631">
          {/* Table Header */}
          <div className="grid grid-cols-3 bg-gray-100 border-b border-gray-200" data-id="element-3632">
            <div className="p-6 font-semibold text-gray-700" data-id="element-3633">Feature</div>
            <div className="p-6 text-center" data-id="element-3634">
              <div className="font-bold text-blue-600 text-xl mb-1" data-id="element-3635">
                ShoPilots
              </div>
              <span className="inline-block bg-green-500 text-white text-xs font-bold px-3 py-1 rounded-full uppercase" data-id="element-3636">
                Winner
              </span>
            </div>
            <div className="p-6 text-center" data-id="element-3637">
              <div className="font-bold text-gray-500 text-xl" data-id="element-3638">Intercom</div>
            </div>
          </div>

          {/* Comparison Rows */}
          {[{
          feature: 'Starting Price',
          icon: <DollarSignIcon className="h-5 w-5" data-id="element-3639" />,
          shopilots: {
            text: '$39/month',
            good: true
          },
          intercom: {
            text: '$39/seat + $0.99/resolution',
            bad: true
          },
          highlight: true
        }, {
          feature: 'Pricing Model',
          icon: <DollarSignIcon className="h-5 w-5" data-id="element-3640" />,
          shopilots: {
            text: 'Simple & Predictable',
            good: true
          },
          intercom: {
            text: 'Complex Usage-Based',
            bad: true
          },
          highlight: true
        }, {
          feature: 'Hidden Fees',
          icon: <XIcon className="h-5 w-5" data-id="element-3641" />,
          shopilots: {
            check: true,
            text: 'None'
          },
          intercom: {
            x: true,
            text: 'Many Add-ons'
          },
          highlight: true
        }, {
          feature: 'Proven Conversion Lift',
          icon: <TrendingUpIcon className="h-5 w-5" data-id="element-3642" />,
          shopilots: {
            text: '+25% Average',
            good: true
          },
          intercom: {
            text: 'Not Reported',
            neutral: true
          }
        }, {
          feature: 'Time to Go Live',
          icon: <ClockIcon className="h-5 w-5" data-id="element-3643" />,
          shopilots: {
            text: 'Minutes',
            good: true
          },
          intercom: {
            text: 'Days to Weeks',
            bad: true
          }
        }, {
          feature: 'Ease of Use',
          icon: <SettingsIcon className="h-5 w-5" data-id="element-3644" />,
          shopilots: {
            text: 'Intuitive UI',
            good: true
          },
          intercom: {
            text: 'Steep Learning Curve',
            bad: true
          }
        }, {
          feature: 'E-Commerce Focused',
          icon: <CheckIcon className="h-5 w-5" data-id="element-3645" />,
          shopilots: {
            check: true,
            text: 'Built for Commerce'
          },
          intercom: {
            text: 'General Purpose',
            neutral: true
          }
        }, {
          feature: 'Voice & Phone Support',
          icon: <CheckIcon className="h-5 w-5" data-id="element-3646" />,
          shopilots: {
            check: true,
            text: 'Included'
          },
          intercom: {
            text: 'Extra Cost',
            bad: true
          }
        }, {
          feature: 'Free Account Migration',
          icon: <CheckIcon className="h-5 w-5" data-id="element-3647" />,
          shopilots: {
            check: true,
            text: 'Yes'
          },
          intercom: {
            x: true,
            text: 'No'
          }
        }, {
          feature: 'Customer Satisfaction',
          icon: <UsersIcon className="h-5 w-5" data-id="element-3648" />,
          shopilots: {
            text: '4.8/5 Rating',
            good: true
          },
          intercom: {
            text: '4.3/5 Rating',
            neutral: true
          }
        }].map((row, index) => <div key={index} className={`grid grid-cols-3 border-b border-gray-100 hover:bg-gray-50 transition-colors ${row.highlight ? 'bg-blue-50' : ''}`} data-id="element-3649">
              <div className="p-6 flex items-center gap-3 font-semibold text-gray-700" data-id="element-3650">
                <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center text-blue-600" data-id="element-3651">
                  {row.icon}
                </div>
                {row.feature}
              </div>
              <div className="p-6 flex items-center justify-center" data-id="element-3652">
                {row.shopilots.check ? <div className="flex items-center gap-2" data-id="element-3653">
                    <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center" data-id="element-3654">
                      <CheckIcon className="h-4 w-4 text-white" data-id="element-3655" />
                    </div>
                    <span className="text-green-600 font-semibold" data-id="element-3656">
                      {row.shopilots.text}
                    </span>
                  </div> : <span className={`font-semibold ${row.shopilots.good ? 'text-green-600' : ''}`} data-id="element-3657">
                    {row.shopilots.text}
                  </span>}
              </div>
              <div className="p-6 flex items-center justify-center" data-id="element-3658">
                {row.intercom.x ? <div className="flex items-center gap-2" data-id="element-3659">
                    <div className="w-6 h-6 bg-gray-300 rounded-full flex items-center justify-center" data-id="element-3660">
                      <XIcon className="h-3 w-3 text-gray-600" data-id="element-3661" />
                    </div>
                    <span className="text-gray-600" data-id="element-3662">{row.intercom.text}</span>
                  </div> : <span className={`font-semibold ${row.intercom.bad ? 'text-red-600' : row.intercom.neutral ? 'text-gray-500' : ''}`} data-id="element-3663">
                    {row.intercom.text}
                  </span>}
              </div>
            </div>)}
        </div>
      </section>

      {/* Key Wins Section */}
      <section className="py-20 bg-white" data-id="element-3664">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-3665">
          <div className="text-center mb-12" data-id="element-3666">
            <h2 className="text-4xl font-bold text-gray-900 mb-4" data-id="element-3667">
              Why Businesses Choose ShoPilots
            </h2>
            <p className="text-xl text-gray-600" data-id="element-3668">
              Real results from our highly engaged customer community
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8" data-id="element-3669">
            {[{
            icon: <DollarSignIcon className="h-8 w-8" data-id="element-3670" />,
            title: 'Transparent Pricing',
            description: "No surprise bills. No per-resolution fees. Know exactly what you'll pay each month with plans starting at just $39/month.",
            metric: 'Save up to 70% vs Intercom'
          }, {
            icon: <TrendingUpIcon className="h-8 w-8" data-id="element-3671" />,
            title: 'Proven Conversions',
            description: "Our AI-powered product recommendations don't just answer questions—they close sales. See measurable revenue impact from day one.",
            metric: '+25% Conversion Rate'
          }, {
            icon: <SettingsIcon className="h-8 w-8" data-id="element-3672" />,
            title: 'Dead Simple Setup',
            description: 'Upload your catalog, customize your AI, go live. No developers needed. No weeks of implementation. Start converting in minutes.',
            metric: 'Go Live in Minutes'
          }, {
            icon: <UsersIcon className="h-8 w-8" data-id="element-3673" />,
            title: 'Growing Community',
            description: "Join 1,500+ e-commerce businesses who've made the switch. Our highly engaged customer base drives product improvements weekly.",
            metric: '1,500+ Happy Customers'
          }].map((win, index) => <div key={index} className="bg-gray-50 rounded-2xl p-8 border-l-4 border-blue-600" data-id="element-3674">
                <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center text-blue-600 mb-6" data-id="element-3675">
                  {win.icon}
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3" data-id="element-3676">
                  {win.title}
                </h3>
                <p className="text-gray-600 mb-4 leading-relaxed" data-id="element-3677">
                  {win.description}
                </p>
                <span className="inline-block bg-green-100 text-green-800 px-4 py-2 rounded-full text-sm font-bold" data-id="element-3678">
                  {win.metric}
                </span>
              </div>)}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-gradient-to-br from-gray-900 to-gray-800 text-white relative overflow-hidden" data-id="element-3679">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-600/10 to-purple-600/10" data-id="element-3680"></div>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10" data-id="element-3681">
          <h2 className="text-4xl md:text-5xl font-bold mb-6" data-id="element-3682">
            Ready to See the Difference?
          </h2>
          <p className="text-xl text-gray-300 mb-8" data-id="element-3683">
            Switch to ShoPilots with our free account migration. No risk, no
            data loss, no hassle.
          </p>
          <div className="flex gap-4 justify-center flex-wrap" data-id="element-3684">
            <a href="https://chat.shopilots.com/signup?product=shopilots" target="_blank" rel="noopener noreferrer" className="inline-flex items-center px-8 py-4 bg-white text-gray-900 rounded-lg font-semibold hover:bg-gray-100 shadow-lg hover:shadow-xl transition-all duration-300" data-id="element-3685">
              Start Free Trial
              <ArrowRightIcon className="ml-2 h-5 w-5" data-id="element-3686" />
            </a>
            <a href="mailto:info@shopilots.com?subject=I%20want%20free%20migration%20to%20your%20product" className="inline-flex items-center px-8 py-4 bg-transparent text-white rounded-lg font-semibold border-2 border-white/30 hover:border-white hover:bg-white/10 transition-all duration-300" data-id="element-3687">
              Request Free Migration
            </a>
          </div>
        </div>
      </section>
    </div>;
};
export default IntercomComparisonPage;